"use client";

import { useEffect, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import { ensureBrowseMenuLoaded } from "@/lib/browse-menu-client-fetch";
import type { BrowseMenuBreadcrumb } from "@/lib/public-product-fetch";

import { type useRouter } from "next/navigation";

import { v4 as uuidv4 } from "uuid";

import Breadcrumbs from "@/components/ui/Breadcrumbs";
import type { Product } from "@/lib/product-utils";
import { browseByMenu } from "@/lib/results-navigation";

type RouterLike = ReturnType<typeof useRouter>;

export type ProductPageReferrer = "modal" | "direct" | "search" | "collection";

const ALLOWED_RETURN_URL_ROOTS = new Set([
  "", // "/"
  "main",
  "results",
  "cart",
  "catalog",
  "collections",
  "chats",
  "chat",
  "brand-explorer",
  "brands",
  "legal",
  "profile",
  "product",
]);

export function useScrollToTopOnProductNavigation(productId: string): void {
  useEffect(() => {
    if (typeof window === "undefined") return;
    const frame = requestAnimationFrame(() => {
      window.scrollTo(0, 0);
    });
    return () => cancelAnimationFrame(frame);
  }, [productId]);
}

/**
 * Shared referrer parsing for both pilot and non-pilot PDP client boundaries.
 */
export function getProductPageReferrer(): ProductPageReferrer {
  if (typeof window === "undefined") {
    return "direct";
  }
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.get("modal_session")) return "modal";
  if (document.referrer.includes("/search")) return "search";
  if (document.referrer.includes("/collection")) return "collection";
  return "direct";
}

/**
 * Shared safe back-navigation behavior for PDP pages.
 */
export function navigateBackToShopping(router: RouterLike): void {
  if (typeof window !== "undefined") {
    const urlParams = new URLSearchParams(window.location.search);
    const returnUrl = urlParams.get("returnUrl");
    // Security: block protocol-relative URLs (e.g. "//evil.com") and limit to known app routes.
    const firstPathSegment = (returnUrl ?? "").split("?")[0].split("/")[1] ?? "";
    const isSafeInternalPath =
      !!returnUrl &&
      returnUrl.startsWith("/") &&
      !returnUrl.startsWith("//") &&
      ALLOWED_RETURN_URL_ROOTS.has(firstPathSegment);

    if (isSafeInternalPath) {
      // Prefer explicit internal return URLs so back-navigation honors source context.
      router.push(returnUrl);
      return;
    }
  }
  router.back();
}

export function getProductBreadcrumbs(product: Product | null): BrowseMenuBreadcrumb[] | null {
  const path = (
    product as (Product & { browse_menu_breadcrumbs?: BrowseMenuBreadcrumb[] | null }) | null
  )?.browse_menu_breadcrumbs;
  if (
    !Array.isArray(path) ||
    path.length === 0 ||
    !path.every(
      (part) =>
        typeof part?.uuid === "string" &&
        part.uuid.trim() &&
        typeof part.display_name === "string" &&
        part.display_name.trim()
    )
  )
    return null;
  return path;
}

interface ProductBreadcrumbsProps {
  productId: string;
  path: BrowseMenuBreadcrumb[] | null;
  router: RouterLike;
}

/** Compact labels render on the server. Only explicit ancestor intent loads facets. */
export function ProductBreadcrumbs({ productId, path, router }: ProductBreadcrumbsProps) {
  const pathname = usePathname();
  const intent = useRef(0);
  const [pending, setPending] = useState(false);
  const [failure, setFailure] = useState<{ uuid: string; message: string } | null>(null);
  useEffect(() => {
    setFailure(null);
    setPending(false);
    return () => {
      intent.current += 1;
    };
  }, [productId, pathname, path]);
  useEffect(() => {
    // Any other navigation intent supersedes an in-flight breadcrumb activation.
    const cancel = (event: MouseEvent) => {
      if (!(event.target instanceof Element) || event.target.closest("[data-product-breadcrumbs]"))
        return;
      if (event.target.closest("a, button")) {
        intent.current += 1;
        setPending(false);
        setFailure(null);
      }
    };
    const pop = () => {
      intent.current += 1;
    };
    document.addEventListener("click", cancel);
    window.addEventListener("popstate", pop);
    return () => {
      document.removeEventListener("click", cancel);
      window.removeEventListener("popstate", pop);
    };
  }, []);
  const activate = async (uuid: string) => {
    const current = ++intent.current;
    setFailure(null);
    setPending(true);
    try {
      const index = await ensureBrowseMenuLoaded();
      if (current !== intent.current) return;
      // Compact API paths contain exact snapshot UUIDs; never guess by label/suffix.
      const node = index.byId.get(uuid);
      if (!node || !node.display_path.length) {
        setFailure({ uuid, message: "This category is no longer available." });
        return;
      }
      browseByMenu(router, uuidv4(), {
        facets: node.search_query.facets ?? [],
        display_path: node.display_path,
        navbar_category: node.display_path[0],
      });
    } catch {
      if (current === intent.current)
        setFailure({ uuid, message: "Category could not be loaded." });
    } finally {
      if (current === intent.current) setPending(false);
    }
  };
  if (!path) return null;
  return (
    <div className="px-6 pt-4 lg:px-[30px] lg:pt-8" data-product-breadcrumbs>
      <Breadcrumbs
        partsOverride={path.map((part) => part.display_name)}
        className="text-xs md:text-sm"
        onSegmentClick={(parts) => {
          const part = path[parts.length - 1];
          if (part) void activate(part.uuid);
        }}
      />
      <div aria-live="polite">
        {pending && <span>Loading category…</span>}
        {failure && (
          <>
            <span>{failure.message}</span>{" "}
            <button onClick={() => void activate(failure.uuid)}>Retry</button>
          </>
        )}
      </div>
    </div>
  );
}
